import { match } from 'ts-pattern';
import { ISelectedFilterItem } from '@modules/filter/domain/models/filterCategory.model';
import { ProductSearchCondition, RecipeSearchCondition, TipsTrickSearchCondition } from '@modules/libs/graphql/generated/graphql';
import { ISearchResult, ISearchResultData, ISearchResultToDomain } from '@modules/search/domain/models/searchResult.model';
import { IGetSearchResultUsecase } from '@modules/search/domain/ports/getSearchResult.usecase.port';
import { canLoadMore, offset } from '@shared/helpers/pagination.helper';
import { ModuleType } from '@shared/types/module.type';
import { left, right } from '@shared/utils/either.utils';
import { IFilterConditions, ISearchResultRepository } from '../ports/searchResult.repository.port';
export class GetSearchResultUsecase implements IGetSearchResultUsecase {
  constructor(private readonly searchResultRepository: ISearchResultRepository) {}
  async handle(keyword: string, category: ModuleType, currentPage: number, pageSize: number, prevData: ISearchResultData[], selectedRecipeFilters: ISelectedFilterItem[], selectedProductFilters: ISelectedFilterItem[], selectedTipsTricksFilters: ISelectedFilterItem[]): IGetSearchResultUsecase.getResultOutput {
    const filterConditions = this._mapFilterConditions(selectedRecipeFilters, selectedProductFilters, selectedTipsTricksFilters);
    const result = await this.searchResultRepository.getResult(keyword, category, offset(currentPage, pageSize), pageSize, filterConditions);
    const value: ISearchResultToDomain = result.value;
    if (result.isLeft()) return left([]);
    const key = this._mapCategoryKey(category);
    const total = this._mapTotalDataKey(category);
    const data = this._mergeData(prevData, value[key]);
    const output: ISearchResult = {
      data: {
        data,
        currentPage,
        canLoadMore: canLoadMore(data.length, value[total])
      },
      totalProducts: value.totalProducts,
      totalRecipes: value.totalRecipes,
      totalTipsTricks: value.totalTipsTricks
    };
    return right(output);
  }
  _mergeData(prevData: ISearchResultData[], currData: ISearchResultData[]): ISearchResultData[] {
    return [...prevData, ...currData];
  }
  _mapCategoryKey(category: ModuleType): string {
    return match(category).with(ModuleType.Product, () => 'product').with(ModuleType.TipsTricks, () => 'tipsAndTricks').otherwise(() => 'recipe');
  }
  _mapTotalDataKey(category: ModuleType): string {
    return match(category).with(ModuleType.Product, () => 'totalProducts').with(ModuleType.TipsTricks, () => 'totalTipsTricks').otherwise(() => 'totalRecipes');
  }
  _mapFilterConditions(selectedRecipeFilters: ISelectedFilterItem[], selectedProductFilters: ISelectedFilterItem[], selectedTipsTricksFilters: ISelectedFilterItem[]): IFilterConditions {
    const recipeCondition: RecipeSearchCondition = selectedRecipeFilters.reduce((prev, curr) => {
      const key = {
        meats: 'meatCategoryIds',
        cooking: 'methodIds',
        foodType: 'typeIds',
        foodNationality: 'regionIds'
      }[curr.categoryId];
      if (key) {
        if (prev[key]) {
          return {
            ...prev,
            [key]: [...prev[key], curr.itemId]
          };
        } else {
          return {
            ...prev,
            [key]: [curr.itemId]
          };
        }
      } else {
        return {
          ...prev
        };
      }
    }, {
      meatCategoryIds: null,
      methodIds: null,
      regionIds: null,
      typeIds: null
    });
    const productCondition: ProductSearchCondition = selectedProductFilters.reduce((prev, curr) => {
      const key = {
        meats: 'meatCategoryIds',
        products: 'productCategoryIds',
        brands: 'brandIds',
        distributionChannel: 'branchIds'
      }[curr.categoryId];
      if (key) {
        if (prev[key]) {
          return {
            ...prev,
            [key]: [...prev[key], curr.itemId]
          };
        } else {
          return {
            ...prev,
            [key]: [curr.itemId]
          };
        }
      } else {
        return {
          ...prev
        };
      }
    }, {
      meatCategoryIds: null,
      productCategoryIds: null,
      brandIds: null,
      branchIds: null
    });
    const tipsTrickCondition: TipsTrickSearchCondition = selectedTipsTricksFilters.reduce((prev, curr) => {
      const key = {
        tipsTiricks: 'categoryIds'
      }[curr.categoryId];
      if (key) {
        if (prev[key]) {
          return {
            ...prev,
            [key]: [...prev[key], curr.itemId]
          };
        } else {
          return {
            ...prev,
            [key]: [curr.itemId]
          };
        }
      } else {
        return {
          ...prev
        };
      }
    }, {
      categoryIds: null
    });
    return {
      recipeCondition,
      productCondition,
      tipsTrickCondition
    };
  }
  _reduceConditionObj(key: string, prevData: Object, currData: ISelectedFilterItem) {
    if (key) {
      if (prevData[key]) {
        return {
          ...prevData,
          [key]: [...prevData[key], currData.itemId]
        };
      } else {
        return {
          ...prevData,
          [key]: [currData.itemId]
        };
      }
    } else {
      return {
        ...prevData
      };
    }
  }
}